import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@medimo/ui";
import { ExternalLink } from "lucide-react";
import {
  type SetInitialPasswordFormSchemaType,
  useSetInitialPasswordForm,
} from "../../hooks/use-set-initial-password-form";
import { PasswordInput } from "../password-input";

type SetInitialPasswordFormProps = {
  isPending: boolean;
  defaultValues: SetInitialPasswordFormSchemaType;
  onSetInitialPassword: VoidFunction;
  onRequireTwoFactor: VoidFunction;
  onSignInError: VoidFunction;
};

export const SetInitialPasswordForm = ({
  isPending,
  defaultValues,
  onSetInitialPassword,
  onRequireTwoFactor,
  onSignInError,
}: SetInitialPasswordFormProps) => {
  const { form, onSubmit } = useSetInitialPasswordForm({
    onSetInitialPassword,
    onRequireTwoFactor,
    onSignInError,
    defaultValues,
  });
  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>メールアドレス</FormLabel>
              <FormControl>
                <Input {...field} type="email" readOnly />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>新しいパスワード</FormLabel>
              <FormControl>
                <PasswordInput role="textbox" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel> 新しいパスワード（確認用）</FormLabel>
              <FormControl>
                <PasswordInput
                  role="textbox"
                  {...field}
                  onBlur={() => {
                    field.onChange(field.value.trim());
                    field.onBlur();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="isTermsOfUseAgreed"
          render={({ field: { value, onChange, ...field } }) => (
            <FormItem className="my-auto">
              <div className="flex items-center gap-3">
                <FormControl>
                  <Checkbox
                    checked={value}
                    onCheckedChange={onChange}
                    {...field}
                  />
                </FormControl>
                <FormLabel className="my-auto flex flex-wrap items-center">
                  <a
                    href="/auth/terms-of-use"
                    target="_blank"
                    className="flex items-center text-info-content underline"
                    rel="noreferrer"
                  >
                    利用規約
                    <ExternalLink />
                  </a>
                  <span>および</span>
                  {/* PP の正本は会社サイトのページ（佐久間さん 2026-09-28 決定）。
                      アプリ同梱の PDF はどこからもリンクされていない */}
                  <a
                    href="https://medimo.ai/privacy-policy"
                    target="_blank"
                    className="flex items-center text-info-content underline"
                    rel="noreferrer"
                  >
                    プライバシーポリシー
                    <ExternalLink />
                  </a>
                  <span>に同意する</span>
                  <span className="text-destructive">*</span>
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button className="font-bold" disabled={isPending}>
          このパスワードを登録
        </Button>
        <div className="flex justify-center">
          <FormMessage>{form.formState.errors.root?.message}</FormMessage>
        </div>
      </form>
    </Form>
  );
};
